<script setup lang="ts">
/**
 * PSkeleton — placeholder de chargement (shimmer subtil sur surface-alt).
 *
 * Variants :
 *  - rect (défaut) : bloc rectangulaire, width + height libres
 *  - text          : ligne de texte, height = 1em par défaut
 *  - circle        : pastille ronde (avatar)
 *
 * Usage pendant un fetch initial pour montrer la STRUCTURE de la page
 * sans devoir attendre les données. Ne pas mettre de skeleton pour une
 * latence < 200ms (l'UX est meilleure avec aucun effet).
 *
 * Pour une liste, utiliser `lines` qui rend N skeletons text empilés.
 */
interface Props {
    variant?: 'rect' | 'text' | 'circle'
    width?: string
    height?: string
    /** Nombre de skeletons text à empiler (variant=text uniquement). */
    lines?: number
}

withDefaults(defineProps<Props>(), {
    variant: 'rect',
    lines: 1,
})
</script>

<template>
    <span v-if="variant === 'text' && lines > 1" class="p-skeleton-group">
        <span
            v-for="n in lines"
            :key="n"
            class="p-skeleton p-skeleton--text"
            :style="{
                width: n === lines ? '60%' : '100%',
            }"
            aria-hidden="true"
        ></span>
    </span>
    <span
        v-else
        :class="['p-skeleton', `p-skeleton--${variant}`]"
        :style="{ width, height }"
        aria-hidden="true"
    ></span>
</template>

<style scoped>
.p-skeleton {
    display: inline-block;
    background: linear-gradient(
        90deg,
        var(--p-color-surface-alt) 0%,
        var(--p-color-line-soft) 50%,
        var(--p-color-surface-alt) 100%
    );
    background-size: 200% 100%;
    animation: p-skeleton-shimmer 1.6s ease-in-out infinite;
    border-radius: var(--p-radius-sm);
}

.p-skeleton--text {
    height: 1em;
    width: 100%;
    vertical-align: middle;
}

.p-skeleton--rect {
    width: 100%;
    height: 1rem;
}

.p-skeleton--circle {
    border-radius: var(--p-radius-full);
}

.p-skeleton-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
}

@keyframes p-skeleton-shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
    .p-skeleton {
        animation: none;
        background: var(--p-color-surface-alt);
    }
}
</style>
